import { AuthUser, ManagedUser, UserRole, Comment } from "../types/auth";

const API = "/api/auth";

export async function getMe(): Promise<AuthUser | null> {
  try {
    const res = await fetch(`${API}/me`, { credentials: "include" });
    if (res.status === 401) return null;
    if (!res.ok) throw new Error("Failed to fetch session");
    const data = await res.json();
    return data.user as AuthUser;
  } catch {
    return null;
  }
}

export async function localLogin(
  username: string,
  password: string
): Promise<AuthUser> {
  const res = await fetch(`${API}/login`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Login failed");
  }
  const data = await res.json();
  return data.user as AuthUser;
}

export async function logout(): Promise<void> {
  await fetch(`${API}/logout`, { method: "POST", credentials: "include" });
}

export async function listConnectedUsers(): Promise<ManagedUser[]> {
  const res = await fetch(`/api/admin/users`, {
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to list users");
  }

  const data = await res.json();
  return (data.users || []) as ManagedUser[];
}

export async function updateUserRole(
  userId: string,
  role: UserRole,
): Promise<ManagedUser> {
  const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/role`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to update user role");
  }

  const data = await res.json();
  return data.user as ManagedUser;
}

/** Redirects the browser to the Azure SSO flow */
export function azureLogin(): void {
  const configuredBackendOrigin = process.env.REACT_APP_BACKEND_ORIGIN?.trim();
  const isLocalHost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  let backendOrigin: string;

  if (configuredBackendOrigin) {
    // Safety: ignore localhost backend config on non-localhost deployments.
    const configuredIsLocalhost = /localhost|127\.0\.0\.1/.test(
      configuredBackendOrigin,
    );
    backendOrigin =
      !isLocalHost && configuredIsLocalhost
        ? window.location.origin
        : configuredBackendOrigin;
  } else {
    backendOrigin = isLocalHost
      ? "http://localhost:3001"
      : window.location.origin;
  }

  window.location.assign(`${backendOrigin}/api/auth/azure`);
}
export async function getTaskComments(taskId: string): Promise<Comment[]> {
  const res = await fetch(`/api/comments?task_id=${encodeURIComponent(taskId)}`, {
    method: "GET",
    credentials: "include",
  });

  if (res.status === 403) {
    // User doesn't have permission to view comments
    return [];
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to fetch comments");
  }

  const data = await res.json();
  return (data.comments || []) as Comment[];
}

export async function createComment(taskId: string, content: string): Promise<Comment> {
  const res = await fetch(`/api/comments`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ taskId, content }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to create comment");
  }

  const data = await res.json();
  return data.comment as Comment;
}